import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function Tier4SuccessBlueprint() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <i className="ri-map-2-line text-2xl"></i>
              <span className="font-semibold text-lg">$1,499</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Tier 4 — Success Blueprint™
            </h1>
            <p className="text-xl md:text-2xl text-purple-50 mb-10 leading-relaxed">
              A complete strategic roadmap built from structured intelligence — showing you how to grow, compete, and scale with clarity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/submit-app"
                className="inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Success Blueprint
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                Download Sample Report
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Who This Is For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              'Founders preparing for major launch',
              'Apps with early traction',
              'Apps needing clarity on their next phase',
              'Founders aiming for investors',
              'Apps stuck in growth plateau',
              'Serious builders seeking a real blueprint'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What You Get
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your Roadmap to Traction and Growth
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-percent-line',
                title: 'Success Probability Assessment',
                description: 'Predicts your app\'s likelihood of traction based on multi-company patterns.'
              },
              {
                icon: 'ri-heart-pulse-line',
                title: 'Emotional Flow Resonance',
                description: 'Measures how well your app triggers positive emotions that lead to engagement and retention.'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Friction Point Mapping',
                description: 'Identifies where users are likely to drop off due to confusion, overload, or design issues.'
              },
              {
                icon: 'ri-fire-line',
                title: 'Desire Trigger Scoring',
                description: 'Evaluates how well your app triggers excitement, value perception, and motivation.'
              },
              {
                icon: 'ri-roadmap-line',
                title: 'Growth Angle Blueprint',
                description: 'Your personalized strategic plan based on data — not instinct or random advice.'
              },
              {
                icon: 'ri-compass-3-line',
                title: '2 Recommended Strategic Moves',
                description: 'Two precise, powerful steps that will shift your traction and positioning.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-indigo-50 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl mb-6">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Why It Matters
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-6">
            Tier 4 gives you what most founders never get:
          </p>
          <p className="text-2xl text-purple-400 font-bold leading-relaxed mb-6">
            a structured, data-backed roadmap showing exactly how to grow your app.
          </p>
          <p className="text-xl text-gray-300 leading-relaxed">
            No guessing, no wasted time, no scattered effort.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <PageCTA 
        title="Start Your Success Blueprint"
        description="Get a complete strategic roadmap for growth and scale."
        primaryText="Get Success Blueprint"
        primaryLink="/submit-app"
        bgGradient="from-purple-600 via-violet-600 to-indigo-600"
      />

      <Footer />
    </div>
  );
}
